import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../lib/axios';
import { footerToParams, getFooterSettings } from '@/components/FooterDateBar';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import GuardianBillingPreview from '@/components/billing/GuardianBillingPreview';
import { Landmark, Search, Loader2, User, CreditCard, BookOpen, Receipt, ShieldCheck, ExternalLink, Mail, Phone, AlertTriangle, X, Archive, Download, Send, MessageSquare, Users, CheckCircle2, Wand2 } from 'lucide-react';
import PhoneLink from '@/components/PhoneLink';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import Layout from '../../components/layout/Layout';
import { DuplicateCheckDialog } from './DuplicateCheckDialog';
import { ArchiveInactiveDialog } from './ArchiveInactiveDialog';
import { BulkPreviewSendDialog } from './BulkPreviewSendDialog';
import { CorrespondenceHistoryTab } from './CorrespondenceHistoryTab';
import BankPeriodHistory from './BankPeriodHistory';
import PersonRemindersPanel from '@/pages/Dashboard/PersonRemindersPanel';
import PersonAppKennungen from '@/components/app/PersonAppKennungen';
import BulkAppKennungen from '@/components/app/BulkAppKennungen';
import { useActionBarConfig } from '../../contexts/ActionBarContext';
import { writeSelectedRecord } from '@/lib/selectedRecord';
import { formatDate, formatScheduleLabel, payerNameLabel, isImportedRemark } from '@/lib/utils';
import { matchesFreeText } from '@/components/search/SearchBuilder';

const fmtEur = (v) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v || 0);
const fmtDate = (d) => (d ? formatDate(d) || '–' : '–');

function Row({ label, value, testId }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100 last:border-0 text-sm">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="text-slate-900 text-right" data-testid={testId}>{value || '–'}</span>
    </div>
  );
}

const ENR_STATUS = { active: ['Aktiv', 'bg-emerald-100 text-emerald-700'], ended: ['Beendet', 'bg-slate-100 text-slate-600'], cancelled: ['Gekündigt', 'bg-rose-100 text-rose-700'] };

export default function PayerAccountsPage() {
  const [search, setSearch] = useState('');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [account, setAccount] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [smsUnread, setSmsUnread] = useState(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(['bank', 'belegungen', 'abrechnung', 'schreiben', 'reminders'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'stammdaten');
  const [sepaGap, setSepaGap] = useState(searchParams.get('filter') === 'sepa-gap');
  const [bankFilter, setBankFilter] = useState({
    q_einzugstag: '',
    q_zahlungsart: '',
    q_zahlungssperre: '',
    q_sepa_mandat: '',
  });
  const [ohneBank, setOhneBank] = useState(searchParams.get('filter') === 'ohne-bank');
  const [cardFilter, setCardFilter] = useState('all'); // 'all' | 'with_sepa' | 'without_sepa'
  const [stats, setStats] = useState(null);
  const [gapCount, setGapCount] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedCount, setArchivedCount] = useState(null);
  const [bankNameGap, setBankNameGap] = useState(0);
  const [fillingBankNames, setFillingBankNames] = useState(false);

  const shown = useMemo(() => list.filter((g) => matchesFreeText({
    name: payerNameLabel(g.last_name, g.first_name),
    students: (g.student_names || []).filter((name) => name && !isImportedRemark(name)),
    address: g.address,
    postal_code: g.postal_code,
    city: g.city,
    gkz: g.gkz,
    email: g.email,
    phone: g.phone,
    notes: g.notes,
    bank_name: g.bank_name,
    bank_account_holder: g.bank_account_holder,
    mandate: g.sepa_mandate_reference,
    iban: g.bank_iban,
    zahlungsart: String(g.payment_type || '').trim().toLowerCase() === 'transfer'
      ? 'Überweiser Überweisung Selbstzahler'
      : 'Abbucher Abbuchung Lastschrift',
  }, search)), [list, search]);

  useEffect(() => {
    if (!detail?.id) return;
    const students = account?.students || [];
    const record = { guardianId: detail.id };
    if (students.length === 1) {
      record.studentId = students[0].id;
      const bookings = students[0].enrollments || [];
      if (bookings.length === 1) record.enrollmentId = bookings[0].id;
    }
    writeSelectedRecord(record);
  }, [detail?.id, account]);

  useActionBarConfig({
    selected: detail ? {
      kind: 'guardian', id: detail.id,
      name: payerNameLabel(detail.last_name, detail.first_name),
      email: detail.email, phone: detail.phone,
    } : null,
    list: { kind: 'guardian', ids: shown.map((g) => g.id) },
  }, [detail?.id, detail?.email, detail?.phone, shown]);

  const [footerTick, setFooterTick] = useState(0);
  useEffect(() => {
    const onFooter = () => setFooterTick((n) => n + 1);
    window.addEventListener('footer-settings-changed', onFooter);
    return () => window.removeEventListener('footer-settings-changed', onFooter);
  }, []);

  const listRequest = useRef(0);
  const loadList = useCallback(async (q, gapOnly, withArchived, sepaCard, missingBank, bank) => {
    const seq = ++listRequest.current;
    setLoading(true);
    setList([]);
    try {
      let has_sepa;
      if (missingBank) has_sepa = undefined;
      else if (gapOnly) has_sepa = false;
      else if (sepaCard === 'with_sepa') has_sepa = true;
      else if (sepaCard === 'without_sepa') has_sepa = false;
      else has_sepa = undefined;

      const period = footerToParams(getFooterSettings());
      const r = await api.get('/guardians', {
        params: {
          is_payer: true,
          search: q || undefined,
          has_sepa,
          has_iban: missingBank ? false : undefined,
          active_only: (gapOnly || missingBank || period.active_only) ? true : undefined,
          period_start: period.period_start,
          period_end: period.period_end,
          active_on: period.active_on,
          include_inactive: withArchived ? true : undefined,
          limit: missingBank ? 500 : 200,
          q_einzugstag: bank?.q_einzugstag || undefined,
          q_zahlungsart: bank?.q_zahlungsart || undefined,
          q_zahlungssperre: bank?.q_zahlungssperre || undefined,
          q_sepa_mandat: bank?.q_sepa_mandat || undefined,
        },
      });
      if (seq !== listRequest.current) return;
      setList(r.data);
    } catch {
      if (seq === listRequest.current) setList([]);
    } finally {
      if (seq === listRequest.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => loadList(search, sepaGap, showArchived, cardFilter, ohneBank, bankFilter), 300);
    return () => clearTimeout(t);
  }, [search, sepaGap, showArchived, cardFilter, ohneBank, bankFilter, loadList, footerTick]);

  const loadStats = useCallback(() => {
    api.get('/guardians/stats').then((r) => {
      setStats(r.data);
      setGapCount(r.data.without_sepa_active);
      setArchivedCount(r.data.archived);
    }).catch(() => {});
    // Der Knopf trägt seine Zahl und bleibt weg, wenn nichts zu tun ist.
    api.get('/guardians/bank-names/offen')
      .then((r) => setBankNameGap(r.data.loesbar || 0))
      .catch(() => setBankNameGap(0));
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  useEffect(() => {
    const gid = searchParams.get('guardian');
    if (gid) open({ id: gid });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (!shown.length) return;
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      e.preventDefault();
      const idx = shown.findIndex((g) => g.id === selected);
      const next = e.key === 'ArrowDown'
        ? Math.min(idx + 1, shown.length - 1)
        : Math.max(idx - 1, 0);
      const g = shown[next];
      if (g && g.id !== selected) {
        open(g);
        document.querySelector(`[data-testid="payer-accounts-item-${g.id}"]`)?.scrollIntoView({ block: 'nearest' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown, selected]);

  const open = async (g) => {
    setSelected(g.id);
    setSmsUnread(0);
    setDetailLoading(true);
    try {
      const [d, a, u] = await Promise.all([
        api.get(`/guardians/${g.id}`),
        api.get(`/guardians/${g.id}/account`),
        api.get(`/guardians/inbound-sms/unread-count`).catch(() => ({ data: { per_guardian: {} } })),
      ]);
      setDetail(d.data);
      setAccount(a.data);
      setSmsUnread((u.data?.per_guardian || {})[g.id] || 0);
    } catch (e) {
      setDetail(null);
      setAccount(null);
      setSelected(null);
    } finally { setDetailLoading(false); }
  };

  const refreshOpen = async (id) => {
    const [d, a] = await Promise.all([
      api.get(`/guardians/${id}`),
      api.get(`/guardians/${id}/account`),
    ]);
    setDetail(d.data);
    setAccount(a.data);
  };

  const refreshAfterMutation = () => {
    loadList(search, sepaGap, showArchived, cardFilter, ohneBank, bankFilter);
    loadStats();
    setDetail(null);
    setAccount(null);
    setSelected(null);
  };

  const setSepaCardFilter = (next) => {
    setSepaGap(false);
    setCardFilter((prev) => (prev === next ? 'all' : next));
  };

  const generateMandateReferences = async () => {
    if (!window.confirm('Für alle Zahlungspflichtigen ohne Mandatsreferenz eine neue generieren?')) return;
    try {
      const res = await api.post('/guardians/generate-mandate-references?prefix=SEPA');
      toast.success(res.data.message);
      refreshAfterMutation();
    } catch {
      toast.error('Fehler beim Generieren');
    }
  };

  const fillBankNames = async () => {
    setFillingBankNames(true);
    try {
      const res = await api.post('/guardians/bank-names/fill');
      toast.success(res.data.message);
      loadList(search, sepaGap, showArchived, cardFilter, ohneBank, bankFilter);
      loadStats();
      // Den geöffneten Zahler neu laden statt die Auswahl zu verwerfen — sonst
      // sieht man den nachgetragenen Namen nicht, den man gerade gesucht hat.
      if (detail?.id) open({ id: detail.id });
    } catch {
      toast.error('Banknamen konnten nicht nachgetragen werden');
    } finally {
      setFillingBankNames(false);
    }
  };

  const exportLetterCsv = async () => {
    try {
      const res = await api.get('/guardians/export/letter-csv', {
        params: { is_payer: true },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `mandate-brief-export_alle_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const rows = res.headers?.['x-rows-written'] || '?';
      toast.success(`Brief-CSV heruntergeladen (${rows} Empfänger)`);
    } catch {
      toast.error('CSV-Export fehlgeschlagen');
    }
  };

  return (
    <Layout>
    <div className="p-6 space-y-5" data-testid="payer-accounts-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Landmark className="h-6 w-6 text-brand-red" /> Zahlungspflichtige
          </h1>
          <p className="text-sm text-slate-500 mt-1">Konten der Zahlungspflichtigen: Stammdaten, Bank/SEPA, Belegungen und Abrechnung</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <BulkAppKennungen personType="guardian" audienceLabel="Zahlungspflichtige" />
          <Button
            variant="outline"
            onClick={generateMandateReferences}
            data-testid="payer-accounts-generate-mandates-btn"
          >
            <Wand2 className="w-4 h-4 mr-2" />
            Mandatsreferenzen generieren
          </Button>
          {bankNameGap > 0 && (
            <Button
              variant="outline"
              onClick={fillBankNames}
              disabled={fillingBankNames}
              data-testid="payer-accounts-fill-bank-names-btn"
              title="Fehlende Banknamen aus IBAN oder BIC ergänzen; vorhandene bleiben unberührt"
            >
              {fillingBankNames
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <Landmark className="w-4 h-4 mr-2" />}
              {bankNameGap} Banknamen nachtragen
            </Button>
          )}
          <Button
            variant="outline"
            onClick={exportLetterCsv}
            className="text-brand-red border-brand-red/30 hover:bg-brand-red/5"
            data-testid="payer-accounts-export-letter-csv-btn"
            title="Als Serienbrief-CSV exportieren (Excel-DE)"
          >
            <Download className="w-4 h-4 mr-2" />
            Brief-CSV
          </Button>
          <BulkPreviewSendDialog filteredIds={list.map((g) => g.id)} />
          <ArchiveInactiveDialog onArchived={refreshAfterMutation} />
          <DuplicateCheckDialog onMerged={refreshAfterMutation} />
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="payer-accounts-stats-cards">
          <button
            type="button"
            onClick={() => { setSepaGap(false); setCardFilter('all'); }}
            className={`text-left transition-all ${!sepaGap && cardFilter === 'all' ? 'ring-2 ring-blue-500 ring-offset-1' : 'hover:shadow-md'}`}
            data-testid="payer-accounts-filter-card-all"
          >
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Zahlungspflichtige</p>
                    <p className="text-2xl font-bold">{stats.payers}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </button>

          <button
            type="button"
            onClick={() => setSepaCardFilter('with_sepa')}
            className={`text-left transition-all ${!sepaGap && cardFilter === 'with_sepa' ? 'ring-2 ring-green-500 ring-offset-1' : 'hover:shadow-md'}`}
            data-testid="payer-accounts-filter-card-with-sepa"
          >
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Mit SEPA-Daten</p>
                    <p className="text-2xl font-bold text-green-600">{stats.with_complete_sepa}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </button>

          <button
            type="button"
            onClick={() => setSepaCardFilter('without_sepa')}
            className={`text-left transition-all ${!sepaGap && cardFilter === 'without_sepa' ? 'ring-2 ring-orange-500 ring-offset-1' : 'hover:shadow-md'}`}
            data-testid="payer-accounts-filter-card-without-sepa"
          >
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Ohne SEPA-Daten</p>
                    <p className="text-2xl font-bold text-orange-600">{stats.without_sepa}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </button>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-brand-red/10 rounded-lg">
                  <CreditCard className="w-5 h-5 text-brand-red" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">SEPA-Abdeckung</p>
                  <p className="text-2xl font-bold">{stats.sepa_coverage_percent}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {ohneBank && (
        <div className="flex items-center gap-2 text-sm text-slate-600 px-1" data-testid="payer-accounts-missing-bank-filter">
          <span>Aktiver Filter:</span>
          <span className="font-semibold text-orange-700">Nur ohne Bankverbindung</span>
          <button type="button" onClick={() => setOhneBank(false)} className="text-brand-red hover:underline ml-1" data-testid="payer-accounts-missing-bank-clear">
            zurücksetzen
          </button>
        </div>
      )}

      {cardFilter !== 'all' && !sepaGap && !ohneBank && (
        <div className="flex items-center gap-2 text-sm text-slate-600 px-1" data-testid="payer-accounts-active-card-filter">
          <span>Aktiver Filter:</span>
          <span className={`font-semibold ${cardFilter === 'with_sepa' ? 'text-green-700' : 'text-orange-700'}`}>
            {cardFilter === 'with_sepa' ? 'Nur mit SEPA-Daten' : 'Nur ohne SEPA-Daten'}
          </span>
          <button type="button" onClick={() => setCardFilter('all')} className="text-brand-red hover:underline ml-1" data-testid="payer-accounts-clear-card-filter">
            zurücksetzen
          </button>
        </div>
      )}

      {stats && stats.payers > 0 && (
        <Card className="bg-gradient-to-r from-brand-red/5 to-slate-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">SEPA-Daten Fortschritt</span>
              <span className="text-sm text-gray-600">
                {stats.with_complete_sepa} von {stats.payers}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-brand-red/50 to-slate-500 h-3 rounded-full transition-all"
                style={{ width: `${stats.sepa_coverage_percent}%` }}
              />
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Liste */}
        <Card className="lg:col-span-1 border-slate-200">
          <CardContent className="p-4">
            <div className="relative mb-3">
              <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Alles durchsuchen…" className="pl-9" data-testid="payer-accounts-search" />
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3" data-testid="payer-accounts-bank-filters">
              {[
                ['q_einzugstag', 'Zahlungstermin', [['1', 'zum 1.'], ['15', 'zum 15.']]],
                ['q_zahlungsart', 'Zahlungstyp', [['sepa', 'SEPA-Lastschrift'], ['transfer', 'Überweisung']]],
                ['q_zahlungssperre', 'Zahlungssperre', [['nein', 'Nein'], ['ja', 'Ja']]],
                ['q_sepa_mandat', 'SEPA-Mandat', [['vorhanden', 'vorhanden'], ['fehlt', 'nicht vorhanden']]],
              ].map(([key, label, options]) => (
                <label key={key} className="block text-[10px] text-slate-500">
                  {label}
                  <select
                    value={bankFilter[key]}
                    onChange={(e) => setBankFilter((prev) => ({ ...prev, [key]: e.target.value }))}
                    className="mt-0.5 w-full h-8 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-800"
                    data-testid={`payer-accounts-${key}`}
                  >
                    <option value="">alle</option>
                    {options.map(([value, text]) => (
                      <option key={value} value={value}>{text}</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
            <button
              onClick={() => {
                setSepaGap((v) => {
                  if (!v) setCardFilter('all');
                  return !v;
                });
              }}
              className={`mb-3 w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border text-xs font-medium transition-colors ${
                sepaGap
                  ? 'border-brand-red/40 bg-amber-50 text-amber-800'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
              data-testid="payer-accounts-sepa-gap-filter"
            >
              <span className="flex items-center gap-1.5">
                <AlertTriangle className={`h-3.5 w-3.5 ${sepaGap ? 'text-brand-warning' : 'text-slate-400'}`} />
                Ohne Bankdaten (aktiver Unterricht)
              </span>
              <span className="flex items-center gap-1.5">
                {gapCount !== null && (
                  <Badge className={`border-0 ${sepaGap ? 'bg-amber-200 text-amber-900' : 'bg-slate-100 text-slate-600'}`} data-testid="payer-accounts-sepa-gap-count">
                    {gapCount}
                  </Badge>
                )}
                {sepaGap && <X className="h-3.5 w-3.5" />}
              </span>
            </button>
            <button
              onClick={() => setShowArchived((v) => !v)}
              className={`mb-3 w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border text-xs font-medium transition-colors ${
                showArchived
                  ? 'border-slate-400 bg-slate-100 text-slate-800'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
              data-testid="payer-accounts-archive-filter"
            >
              <span className="flex items-center gap-1.5">
                <Archive className={`h-3.5 w-3.5 ${showArchived ? 'text-slate-700' : 'text-slate-400'}`} />
                Archiv anzeigen
              </span>
              <span className="flex items-center gap-1.5">
                {archivedCount !== null && (
                  <Badge className="border-0 bg-slate-100 text-slate-600" data-testid="payer-accounts-archive-count">{archivedCount}</Badge>
                )}
                {showArchived && <X className="h-3.5 w-3.5" />}
              </span>
            </button>
            {loading ? (
              <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div>
            ) : (
              <div className="max-h-[calc(100dvh-16rem)] overflow-y-auto divide-y divide-slate-100" data-testid="payer-accounts-list">
                {shown.map((g) => (
                  <button
                    key={g.id}
                    onClick={() => open(g)}
                    className={`w-full text-left px-2 py-2.5 hover:bg-slate-50 rounded transition-colors ${selected === g.id ? 'bg-brand-red/5' : ''}`}
                    data-testid={`payer-accounts-item-${g.id}`}
                  >
                    <div className="text-sm font-medium text-slate-900">{payerNameLabel(g.last_name, g.first_name)}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-2">
                      {(g.student_names || []).filter((name) => name && !isImportedRemark(name)).slice(0, 2).join(', ')}
                      {g.has_sepa_data && <ShieldCheck className="h-3 w-3 text-emerald-600" title="SEPA-Daten vorhanden" />}
                    </div>
                  </button>
                ))}
                {shown.length === 0 && <div className="py-8 text-center text-sm text-slate-400">Keine Zahlungspflichtigen gefunden</div>}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail */}
        <Card className="lg:col-span-2 border-slate-200 min-w-0 overflow-hidden flex flex-col">
          <CardContent className="p-0 min-w-0 overflow-y-auto max-h-[calc(100dvh-8rem)]" data-testid="payer-accounts-detail-scroll">
            <div className="px-5 pt-5 pb-10">
            {!detail && !detailLoading && (
              <div className="py-20 text-center text-slate-400 text-sm">Bitte links eine/n Zahlungspflichtige/n auswählen</div>
            )}
            {detailLoading && <div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin inline text-slate-400" /></div>}
            {detail && !detailLoading && (
              <div data-testid="payer-accounts-detail">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                      {payerNameLabel(detail.last_name, detail.first_name)}
                      {detail.is_active === false && (
                        <>
                          <Badge className="border-0 bg-slate-200 text-slate-600">Archiviert</Badge>
                          <button
                            onClick={async () => {
                              await api.put(`/guardians/${detail.id}`, { is_active: true });
                              setDetail({ ...detail, is_active: true });
                              loadList(search, sepaGap, showArchived, cardFilter, ohneBank, bankFilter);
                              loadStats();
                            }}
                            className="text-xs text-brand-red hover:underline font-normal"
                            data-testid="payer-accounts-reactivate-btn"
                          >
                            Reaktivieren
                          </button>
                        </>
                      )}
                    </h2>
                    <div className="text-sm text-slate-500" data-testid="payer-accounts-header-contact">{detail.email || 'keine E-Mail'}</div>
                  </div>
                  {account && (
                    <div className="text-right">
                      <div className="text-xs text-slate-500">Offener Betrag</div>
                      <div className={`text-lg font-bold ${account.open_amount > 0 ? 'text-rose-600' : 'text-emerald-600'}`} data-testid="payer-accounts-open-amount">
                        {fmtEur(account.open_amount)}
                      </div>
                    </div>
                  )}
                </div>

                <Tabs value={tab} onValueChange={setTab} className="w-full min-w-0">
                  <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
                    <TabsTrigger value="stammdaten" data-testid="payer-accounts-tab-stammdaten" className="shrink-0">
                      <User className="h-3.5 w-3.5 mr-1 shrink-0" /> Stammdaten
                    </TabsTrigger>
                    <TabsTrigger value="app" data-testid="payer-accounts-tab-app" className="shrink-0">
                      App
                    </TabsTrigger>
                    <TabsTrigger value="bank" data-testid="payer-accounts-tab-bank" className="shrink-0">
                      <CreditCard className="h-3.5 w-3.5 mr-1 shrink-0" /> Bank / SEPA
                    </TabsTrigger>
                    <TabsTrigger value="belegungen" data-testid="payer-accounts-tab-belegungen" className="shrink-0">
                      <BookOpen className="h-3.5 w-3.5 mr-1 shrink-0" /> Belegungen
                    </TabsTrigger>
                    <TabsTrigger value="abrechnung" data-testid="payer-accounts-tab-abrechnung" className="shrink-0">
                      <Receipt className="h-3.5 w-3.5 mr-1 shrink-0" /> Abrechnung
                    </TabsTrigger>
                    <TabsTrigger value="schreiben" data-testid="payer-accounts-tab-schreiben" className="shrink-0">
                      <Mail className="h-3.5 w-3.5 mr-1 shrink-0" /> Schreiben
                      {smsUnread > 0 && (
                        <Badge className="ml-1.5 border-0 bg-emerald-600 text-white text-[10px] px-1.5 py-0" data-testid="payer-accounts-tab-schreiben-badge">
                          {smsUnread} neu
                        </Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger
                      value="reminders"
                      data-testid="payer-accounts-tab-reminders"
                      className="shrink-0"
                      title="Nachrichten / Erinnerungen"
                    >
                      <MessageSquare className="h-3.5 w-3.5 mr-1 shrink-0" /> Erinnerungen
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="stammdaten" className="mt-4">
                    <Row label="Name" value={payerNameLabel(detail.last_name, detail.first_name)} testId="payer-accounts-name" />
                    <Row label="Straße" value={detail.address} />
                    <Row label="PLZ / Ort" value={[detail.postal_code, detail.city].filter(Boolean).join(' ')} />
                    <Row label="Gkz" value={detail.gkz} testId="payer-accounts-gkz" />
                    <Row label="E-Mail" value={detail.email} />
                    <Row label="Telefon" value={detail.phone ? <PhoneLink phone={detail.phone} /> : null} />
                    <Row label="Zahlungspflichtig" value={detail.is_payer ? 'Ja' : 'Nein'} />
                    <Row label="Erziehungsberechtigt" value={detail.has_custody ? 'Ja' : 'Nein'} />
                    {(account?.weitere_erziehungsberechtigte || []).map((person) => (
                      <Row
                        key={person.id}
                        label="Weiterer Erziehungsberechtigter"
                        testId={`payer-weitere-${person.id}`}
                        value={(
                          <button
                            type="button"
                            className="font-medium hover:underline"
                            onClick={() => navigate(`/guardians?guardian=${person.id}`)}
                          >
                            {person.name}
                          </button>
                        )}
                      />
                    ))}
                    {detail.notes && !isImportedRemark(detail.notes) && <Row label="Notizen" value={detail.notes} />}

                    <div className="mt-6" data-testid="payer-accounts-family">
                      <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Familienmitglieder im Unterricht</div>
                      {account?.students?.filter((s) => s.has_enrollments).length ? (
                        account.students.filter((s) => s.has_enrollments).map((s) => (
                          <div key={s.id} className="flex items-start justify-between gap-4 py-2 border-b border-slate-100 last:border-0" data-testid={`payer-accounts-family-member-${s.id}`}>
                            <div className="min-w-0">
                              <button
                                onClick={() => navigate(`/students/${s.id}`)}
                                className="text-sm font-medium text-slate-900 hover:text-brand-red transition-colors flex items-center gap-1"
                                data-testid={`payer-accounts-family-link-${s.id}`}
                              >
                                {s.name}{s.instrument ? ` · ${s.instrument}` : ''}
                                <ExternalLink className="h-3 w-3 text-slate-400" />
                              </button>
                              {(s.email && s.email !== detail.email) || (s.phone && s.phone !== detail.phone) ? (
                                <div className="text-xs text-slate-500 mt-0.5 space-x-3">
                                  {s.email && s.email !== detail.email && (
                                    <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{s.email}</span>
                                  )}
                                  {s.phone && s.phone !== detail.phone && (
                                    <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{s.phone}</span>
                                  )}
                                </div>
                              ) : null}
                            </div>
                            <div className="text-right shrink-0">
                              <Badge className={`border-0 ${s.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                                {s.is_active ? 'Aktiv' : 'Inaktiv'}
                              </Badge>
                              <div className="text-xs text-slate-500 mt-0.5" data-testid={`payer-accounts-family-since-${s.id}`}>
                                {s.is_active
                                  ? (s.active_since ? `aktiv seit: ${fmtDate(s.active_since)}` : '')
                                  : (s.inactive_since ? `inaktiv seit: ${fmtDate(s.inactive_since)}` : '')}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-slate-400 py-3">Keine Unterrichtsteilnehmer in der Familie gefunden</div>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="bank" className="mt-4">
                    <BankPeriodHistory guardian={detail} onSaved={() => refreshOpen(detail.id)} />
                  </TabsContent>

                  <TabsContent value="belegungen" className="mt-4 space-y-4">
                    {account?.students?.length ? account.students.map((s) => (
                      <div key={s.id} data-testid={`payer-accounts-student-${s.id}`}>
                        <button
                          onClick={() => navigate(`/students/${s.id}`)}
                          className="text-sm font-semibold text-slate-800 mb-1 hover:text-brand-red transition-colors flex items-center gap-1"
                          data-testid={`payer-accounts-student-link-${s.id}`}
                        >
                          {s.name}{s.instrument ? ` · ${s.instrument}` : ''}
                          <ExternalLink className="h-3 w-3 text-slate-400" />
                        </button>
                        {s.enrollments.length ? (
                          <table className="w-full text-sm table-fixed">
                            <tbody>
                              {s.enrollments.map((e) => {
                                const [lbl, cls] = ENR_STATUS[e.status] || [e.status, 'bg-slate-100 text-slate-600'];
                                const openInUv = () => {
                                  navigate(`/payer-accounts?guardian=${detail.id}&tab=belegungen`, { replace: true });
                                  navigate(`/unterrichtsverwaltung?enrollment_id=${e.id}`);
                                };
                                return (
                                  <tr
                                    key={e.id}
                                    onClick={openInUv}
                                    className="group border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50 transition-colors"
                                    title="Belegung in der Unterrichtsverwaltung öffnen"
                                    data-testid={`payer-accounts-enrollment-${e.id}`}
                                  >
                                    <td className="w-[42%] py-1.5 pr-2 text-slate-900 group-hover:text-brand-red">{formatScheduleLabel(e.course_name)}</td>
                                    <td className="w-[22%] py-1.5 pr-2 text-slate-500" data-testid={`payer-accounts-enrollment-teacher-${e.id}`}>{e.teacher_name || ''}</td>
                                    <td className="w-[24%] py-1.5 pr-2 text-slate-500 whitespace-nowrap" data-testid={`payer-accounts-enrollment-dates-${e.id}`}>{fmtDate(e.start_date)}{e.end_date ? ` – ${fmtDate(e.end_date)}` : ''}</td>
                                    <td className="w-[12%] py-1.5 text-right"><Badge className={`${cls} border-0`}>{lbl}</Badge></td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        ) : <div className="text-xs text-slate-400">Keine Belegungen</div>}
                      </div>
                    )) : <div className="text-sm text-slate-400 py-6 text-center">Keine verknüpften Schüler gefunden</div>}
                  </TabsContent>

                  <TabsContent value="schreiben" className="mt-4">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Versand-Historie (E-Mails, Briefe & SMS)</div>
                    <CorrespondenceHistoryTab guardianId={detail.id} onUnreadChanged={setSmsUnread} />
                  </TabsContent>

                  <TabsContent value="reminders" className="mt-4">
                    <PersonRemindersPanel
                      personType="guardian"
                      personId={detail.id}
                      personLabel={payerNameLabel(detail.last_name, detail.first_name)}
                      compact
                    />
                  </TabsContent>

                  <TabsContent value="app" className="mt-4">
                    <PersonAppKennungen personType="guardian" personId={detail.id} />
                  </TabsContent>

                  <TabsContent value="abrechnung" className="mt-4">
                    <GuardianBillingPreview
                      guardian={detail}
                      billingPreview={account?.billing_preview}
                      invoices={account?.invoices}
                    />
                  </TabsContent>
                </Tabs>
              </div>
            )}
            </div>
          </CardContent>
        </Card>
      </div>

    </div>
    </Layout>
  );
}
